import React from "react";
import { Link } from "react-router";
import "./Footer.css";
import { openConsentSettings } from "../../consent/consent";
import { imageServerUrl, imageSizes } from "../../constants/config";
import { Configuration, CustomerContactData } from "../Types/Configuration";
import { FaFacebookSquare } from "react-icons/fa";
import { FaInstagram } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { AiFillTikTok } from "react-icons/ai";

interface FooterProps {
  footerData: Configuration;
  contactData: CustomerContactData;
  // A Tag Manager container is active, so there is a consent choice to revisit.
  showConsentSettings?: boolean;
}

const Footer: React.FC<FooterProps> = ({ footerData, contactData, showConsentSettings = false }) => {
  const currentYear = new Date().getFullYear();
  const companyName = contactData?.companyName || contactData.name;

  const socialLinks = [
    { key: "facebook", href: contactData.facebookLink, Icon: FaFacebookSquare as any },
    { key: "instagram", href: contactData.instagramLink, Icon: FaInstagram as any },
    { key: "x", href: contactData.xLink, Icon: FaXTwitter as any },
    { key: "tikTok", href: contactData.tikTokLink, Icon: AiFillTikTok as any },
  ].filter((s) => !!s.href);

  return (
    <footer className="footer">
      {/* Logo + Social icons */}
      <div className="footer-top">
        <img
          src={`${imageServerUrl}/image/${imageSizes.thumbnail}/${footerData.logoImageId}`}
          alt={companyName}
          className="footer-logo-img"
        />
        {socialLinks.length > 0 && (
          <div className="footer-social">
            {socialLinks.map(({ key, href, Icon }) => (
              <a
                key={key}
                className="footer-icon"
                href={href!}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={key}
              >
                <Icon />
              </a>
            ))}
          </div>
        )}
      </div>

      <hr className="footer-divider" />

      {/* Description, contact, address */}
      <div className="footer-middle">
        {footerData?.footerDescription && (
          <div className="footer-description-col">
            <div className="footer-section-title">Meistä</div>
            <p className="footer-description">{footerData.footerDescription}</p>
          </div>
        )}

        <div className="footer-contact-col">
          <div className="footer-section-title">Yhteystiedot</div>
          {contactData.email && (
            <a className="footer-contact-link" href={`mailto:${contactData.email}`}>
              {contactData.email}
            </a>
          )}
          {contactData.phone && (
            <a className="footer-contact-link" href={`tel:${contactData.phone}`}>
              {contactData.phone}
            </a>
          )}
        </div>

        <div className="footer-address-col">
          <div className="footer-section-title">Osoite</div>
          {contactData.address && <span>{contactData.address}</span>}
          {(contactData.postalCode || contactData.city) && (
            <span>
              {[contactData.postalCode, contactData.city].filter(Boolean).join(" ")}
            </span>
          )}
          {contactData.country && <span>{contactData.country}</span>}
        </div>
      </div>

      {/* Copyright + legal */}
      <div className="footer-bottom">
        <span className="footer-copyright">
          © {currentYear} {companyName}
        </span>
        <nav className="footer-legal" aria-label="Käytännöt">
          <Link to="/evastekaytanto" className="footer-legal-link">
            Evästekäytäntö
          </Link>
          {showConsentSettings && (
            <button type="button" className="footer-legal-link" onClick={openConsentSettings}>
              Evästeasetukset
            </button>
          )}
        </nav>
      </div>
    </footer>
  );
};

export default Footer;
